'use client'

// React
import { useEffect, useState } from 'react'

// Next.js
import { usePathname } from 'next/navigation'

// Animation
import { motion, AnimatePresence } from 'framer-motion'

interface Heading {
  id: string
  text: string
  level: number
}

export function TableOfContents() {
  const [headings, setHeadings] = useState<Heading[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const pathname = usePathname()

  useEffect(() => {
    let observer: IntersectionObserver | null = null

    // Re-scan headings when pathname changes
    const scanHeadings = () => {
      const article = document.querySelector('article')
      if (!article) return []

      return Array.from(article.querySelectorAll('h1, h2, h3'))
        .filter((element) => element.id) // Only include headings with IDs
        .map((element) => ({
          id: element.id,
          text: element.textContent || '',
          level: Number(element.tagName.charAt(1)),
        }))
    }

    // Use setTimeout to ensure DOM is updated after navigation
    const timer = setTimeout(() => {
      const elements = scanHeadings()
      setHeadings(elements)

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveId(entry.target.id)
            }
          })
        },
        { rootMargin: '0px 0px -80% 0px' }
      )

      elements.forEach((heading) => {
        const element = document.getElementById(heading.id)
        if (element) observer?.observe(element)
      })
    }, 100)

    return () => {
      clearTimeout(timer)
      observer?.disconnect()
    }
  }, [pathname])

  const hasHeadings = headings.length > 0

  return (
    <nav className="sticky top-20 hidden max-h-[calc(100vh-5rem)] w-64 overflow-y-auto overscroll-contain pl-8 lg:top-22.5 lg:block">
      <h4 className="mb-4 text-sm font-semibold tracking-wider text-slate-900 uppercase dark:text-slate-100">
        On This Page
      </h4>
      <AnimatePresence mode="wait">
        {hasHeadings && (
          <motion.div
            key={pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.3, delay: 0.3 } }}
            exit={{ opacity: 0, transition: { duration: 0.0 } }}
          >
            <ul className="scrollbar-thin space-y-1 pr-2 pb-20 text-sm">
              {headings.map((heading) => (
                <li key={heading.id} style={{ marginLeft: (heading.level - 1) * 12 }}>
                  <a
                    href={`#${heading.id}`}
                    className={`hover:text-primary-600 dark:hover:text-primary-400 block border-l-2 py-1 pl-3 transition-colors ${
                      activeId === heading.id
                        ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400 font-medium'
                        : 'border-slate-200 text-slate-600 dark:border-slate-800 dark:text-slate-300'
                    }`}
                  >
                    {heading.text}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}
